import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";
import { bestPromo, discountFor, fetchActivePromos, promoLabel, validatePromoCode } from "@/lib/promo";
import { PageHero, Reveal, EmptyState } from "@/components/shared";
import { CATEGORIES } from "@/pages/Carte";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LUNCH = ["11:45", "12:00", "12:15", "12:30", "12:45", "13:00", "13:15", "13:30", "13:45", "14:00"];
const DINNER = ["19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00", "22:30", "23:00"];

const parsePrice = (price) => {
  const n = parseFloat((price || "").replace("€", "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const fmt = (n) => n.toFixed(2).replace(".", ",") + " €";

export default function AEmporter() {
  const [menu, setMenu] = useState(null);
  const [categories, setCategories] = useState(CATEGORIES);
  const [active, setActive] = useState("entrees");
  const [cart, setCart] = useState({});
  const [form, setForm] = useState({ name: "", phone: "", email: "", date: "", time: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [autoPromos, setAutoPromos] = useState([]);
  const [codePromo, setCodePromo] = useState(null);
  const [codeInput, setCodeInput] = useState("");

  useEffect(() => {
    api.get("/menu").then((r) => setMenu(r.data)).catch(() => setMenu({}));
    fetchActivePromos("emporter").then(setAutoPromos);
    api
      .get("/categories")
      .then((r) => {
        if (r.data.length) {
          setCategories(r.data);
          setActive(r.data[0].id);
        }
      })
      .catch(() => {});
  }, []);

  const items = (menu?.[active] || []).filter((i) => parsePrice(i.price) > 0 && i.takeaway !== false);
  const cartList = Object.values(cart);
  const total = cartList.reduce((s, c) => s + parsePrice(c.item.price) * c.qty, 0);
  const { promo, discount } = bestPromo(autoPromos, codePromo, total);
  const totalAfterPromo = Math.round((total - discount) * 100) / 100;
  const percentPromo = promo?.type === "percent" ? promo : null;

  const applyCode = async () => {
    if (!codeInput.trim()) return;
    try {
      const p = await validatePromoCode(codeInput.trim(), "emporter");
      setCodePromo(p);
      toast.success(`Code « ${p.name} » appliqué.`);
    } catch {
      setCodePromo(null);
      toast.error("Code promotionnel invalide ou expiré.");
    }
  };

  const add = (item) => {
    trackEvent("add_to_cart", { name: item.name_fr, path: "/a-emporter" });
    setCart((c) => ({ ...c, [item.id]: { item, qty: (c[item.id]?.qty || 0) + 1 } }));
  };
  const dec = (id) =>
    setCart((c) => {
      const qty = (c[id]?.qty || 0) - 1;
      const next = { ...c };
      if (qty <= 0) delete next[id];
      else next[id] = { ...next[id], qty };
      return next;
    });
  const removeItem = (id) =>
    setCart((c) => {
      const next = { ...c };
      delete next[id];
      return next;
    });

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const pay = async () => {
    if (cartList.length === 0) {
      toast.error("Votre commande est vide.");
      return;
    }
    if (!form.name || !form.phone || !form.email || !form.date || !form.time) {
      toast.error("Merci de compléter vos coordonnées et le créneau de retrait.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/orders", {
        ...form,
        items: cartList.map((c) => ({ menu_item_id: c.item.id, quantity: c.qty })),
        promo_code: codePromo ? codeInput.trim().toUpperCase() : "",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSending(false);
    }
  };

  return (
    <div data-testid="a-emporter-page">
      <PageHero overline="À Emporter" title="Commandez à emporter" testId="emporter-hero">
        <p>
          Composez votre commande, réglez en ligne de façon sécurisée, puis venez la récupérer au
          restaurant au créneau choisi.
        </p>
      </PageHero>

      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1fr_380px]">
          {/* Menu */}
          <div>
            <Reveal>
              <div className="flex flex-wrap gap-2 border-b border-white/10 pb-6" data-testid="order-categories">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActive(cat.id)}
                    data-testid={`order-tab-${cat.id}`}
                    className={`rounded-md px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                      active === cat.id ? "bg-copper text-ivory" : "text-ivory/50 hover:text-gold"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </Reveal>

            <div className="mt-10 space-y-4">
              {menu === null ? null : items.length === 0 ? (
                <EmptyState text="Aucun article disponible dans cette catégorie." />
              ) : (
                items.map((item) => {
                  const inCart = cart[item.id];
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-4 rounded-lg border border-white/10 bg-coal/60 px-5 py-4"
                      data-testid={`order-item-${item.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                          <h3 className="truncate text-sm font-semibold text-ivory">{item.name_fr}</h3>
                          {percentPromo ? (
                            <span className="shrink-0 text-sm font-semibold text-gold" data-testid={`order-promo-price-${item.id}`}>
                              <span className="mr-2 text-xs font-normal text-ivory/35 line-through">{item.price}</span>
                              {fmt(parsePrice(item.price) * (1 - percentPromo.value / 100))}
                            </span>
                          ) : (
                            <span className="shrink-0 text-sm font-semibold text-gold">{item.price}</span>
                          )}
                        </div>
                        {item.description_fr && (
                          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-ivory/45">
                            {item.description_fr}
                          </p>
                        )}
                      </div>
                      {inCart ? (
                        <div className="flex shrink-0 items-center gap-2">
                          <button onClick={() => dec(item.id)} aria-label="Retirer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold" data-testid={`order-dec-${item.id}`}>
                            <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </button>
                          <span className="w-6 text-center text-sm font-semibold text-ivory">{inCart.qty}</span>
                          <button onClick={() => add(item)} aria-label="Ajouter" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold" data-testid={`order-inc-${item.id}`}>
                            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => add(item)}
                          className="btn-outline !h-9 shrink-0 !px-4 !text-[11px]"
                          data-testid={`order-add-${item.id}`}
                        >
                          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Ajouter
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Panier */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-white/10 bg-coal/60 p-6" data-testid="order-cart">
              <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-ivory">
                <ShoppingBag className="h-4 w-4 text-gold" strokeWidth={1.5} />
                Votre commande
              </h2>

              {cartList.length === 0 ? (
                <p className="mt-6 text-sm text-ivory/40" data-testid="cart-empty">
                  Ajoutez des articles depuis la carte.
                </p>
              ) : (
                <>
                <ul className="mt-6 space-y-3" data-testid="cart-items">
                  {cartList.map((c) => (
                    <li key={c.item.id} className="flex items-center gap-3 text-sm">
                      <span className="text-gold">{c.qty}×</span>
                      <span className="min-w-0 flex-1 truncate text-ivory/80">{c.item.name_fr}</span>
                      <span className="text-ivory/60">{fmt(parsePrice(c.item.price) * c.qty)}</span>
                      <button onClick={() => removeItem(c.item.id)} aria-label="Supprimer" className="text-ivory/30 transition-colors hover:text-red-400" data-testid={`cart-remove-${c.item.id}`}>
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </li>
                  ))}
                </ul>
                {(() => {
                  const pairings = [...new Set(cartList.map((c) => c.item.pairings).filter(Boolean))];
                  if (pairings.length === 0) return null;
                  return (
                    <p className="mt-4 rounded-md border border-gold/20 bg-gold/5 px-4 py-3 text-xs leading-relaxed text-gold/80" data-testid="cart-pairings">
                      Nos suggestions d'accompagnement : {pairings.join(" · ")}
                    </p>
                  );
                })()}
                </>
              )}

              <div className="mt-6 space-y-2 border-t border-white/10 pt-4">
                <div className="flex items-center gap-2">
                  <input
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value)}
                    placeholder="Code promo"
                    className="input-dark !h-10 flex-1 uppercase !text-xs"
                    data-testid="cart-promo-input"
                  />
                  <button onClick={applyCode} className="btn-outline !h-10 !px-4 !text-[11px]" data-testid="cart-promo-apply">
                    Appliquer
                  </button>
                </div>
                {discount > 0 && (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-ivory/50">Sous-total</span>
                      <span className="text-ivory/50 line-through">{fmt(total)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm" data-testid="cart-discount">
                      <span className="text-gold">{promo.name} ({promoLabel(promo)})</span>
                      <span className="text-gold">-{fmt(discount)}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-sm text-ivory/60">Total</span>
                  <span className="text-lg font-bold text-gold" data-testid="cart-total">
                    {fmt(totalAfterPromo)}
                  </span>
                </div>
              </div>

              <div className="mt-6 space-y-4 border-t border-white/10 pt-6">
                <input value={form.name} onChange={update("name")} placeholder="Nom et prénom" className="input-dark !h-11" data-testid="order-name-input" />
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <input value={form.phone} onChange={update("phone")} placeholder="Téléphone" className="input-dark !h-11" data-testid="order-phone-input" />
                  <input type="email" value={form.email} onChange={update("email")} placeholder="Email" className="input-dark !h-11" data-testid="order-email-input" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <input type="date" value={form.date} onChange={update("date")} min={new Date().toISOString().split("T")[0]} className="input-dark !h-11 [color-scheme:dark]" data-testid="order-date-input" />
                  <Select value={form.time} onValueChange={(v) => setForm((f) => ({ ...f, time: v }))}>
                    <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="order-time-select">
                      <SelectValue placeholder="Retrait" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-coal text-ivory">
                      <SelectGroup>
                        <SelectLabel className="text-gold">Midi</SelectLabel>
                        {LUNCH.map((t) => (
                          <SelectItem key={t} value={t}>{t.replace(":", "h")}</SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-gold">Soir</SelectLabel>
                        {DINNER.map((t) => (
                          <SelectItem key={t} value={t}>{t.replace(":", "h")}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <textarea value={form.notes} onChange={update("notes")} placeholder="Précisions (allergies, demandes…)" rows={2} className="input-dark !h-auto py-3" data-testid="order-notes-input" />
                <button onClick={pay} disabled={sending || cartList.length === 0} className="btn-primary w-full" data-testid="order-pay-button">
                  <CreditCard className="h-4 w-4" strokeWidth={1.5} />
                  {sending ? "Redirection…" : `Payer — ${fmt(totalAfterPromo)}`}
                </button>
                <p className="text-center text-[11px] text-ivory/35">Paiement sécurisé par Stripe.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/orders/payment-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      attempts += 1;
      if (attempts < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="order-success-page">
      <PageHero
        overline="À Emporter"
        title={state === "paid" ? "Commande confirmée" : "Confirmation en cours"}
        testId="emporter-hero"
      />
      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <div className="rounded-lg border border-gold/30 bg-coal/60 p-12 text-center">
              {state === "loading" && (
                <p className="text-sm text-ivory/60" data-testid="order-payment-loading">
                  Vérification du paiement…
                </p>
              )}
              {state === "paid" && (
                <>
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40">
                    <Check className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  </span>
                  <h2 className="mt-8 text-2xl font-semibold tracking-tight text-ivory">Merci !</h2>
                  <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-ivory/55" data-testid="order-payment-confirmed">
                    Votre commande est confirmée et réglée. Un email de confirmation vient de vous
                    être envoyé. Présentez-vous au restaurant au créneau choisi pour la récupérer.
                  </p>
                </>
              )}
              {state === "pending" && (
                <p className="text-sm text-ivory/60" data-testid="order-payment-pending">
                  Paiement en cours de traitement. Vous recevrez un email de confirmation.
                </p>
              )}
              {state === "error" && (
                <p className="text-sm text-red-400" data-testid="order-payment-error">
                  Impossible de vérifier le paiement. Merci de nous contacter sur WhatsApp.
                </p>
              )}
              <Link to="/" className="btn-outline mt-10" data-testid="order-success-home">
                Retour à l'accueil
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
